import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { RetentionView } from '../lib/types';
import { useResource } from '../lib/useResource';
import { useSession } from '../lib/session';
import { Card, Empty, Loading, Notice, exactly } from '../components/ui';

const CLASS_DESCRIPTIONS: Record<string, string> = {
  SECURITY: 'Malware detections, suspicious process behaviour, failed logon bursts.',
  POSTURE: 'Encryption, firewall, patch and anti-malware state over time.',
  DLP: 'Removable storage attachments and blocked transfers.',
  ACTIVITY: 'Foreground application and active-hours records. The shortest window, deliberately.',
  SOFTWARE: 'Installation, removal and version changes.',
  SYSTEM: 'Agent lifecycle, policy application and command outcomes.',
};

export function Retention() {
  const { can } = useSession();
  const [edits, setEdits] = useState<Record<string, { days: string; justification: string }>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const policies = useResource(() => api.get<RetentionView[]>('/api/v1/retention'), []);

  async function save(eventClass: string) {
    const edit = edits[eventClass];
    if (!edit) return;

    setBusy(true);
    setMessage(null);
    try {
      await api.put(`/api/v1/retention/${eventClass}`, {
        retentionDays: Number(edit.days),
        justification: edit.justification,
      });
      setMessage({ kind: 'ok', text: `${eventClass} retention updated.` });
      setEdits((current) => {
        const next = { ...current };
        delete next[eventClass];
        return next;
      });
      policies.reload();
    } catch (caught) {
      setMessage({ kind: 'error', text: caught instanceof ApiError ? caught.message : 'Update failed' });
    } finally {
      setBusy(false);
    }
  }

  async function purgeNow() {
    setBusy(true);
    setMessage(null);
    try {
      const deleted = await api.post<Record<string, number>>('/api/v1/retention/purge');
      const total = Object.values(deleted).reduce((sum, count) => sum + count, 0);
      setMessage({ kind: 'ok', text: `Purge complete: ${total} row(s) permanently deleted.` });
      policies.reload();
    } catch (caught) {
      setMessage({ kind: 'error', text: caught instanceof ApiError ? caught.message : 'Purge failed' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Retention</h1>
        <p>
          Storage limitation under the Nigeria Data Protection Act 2023. Data past its window is
          hard-deleted by a nightly job &mdash; not flagged, not archived. The audit trail is
          excluded and kept for its own regulatory period.
        </p>
      </div>

      {message && <Notice kind={message.kind}>{message.text}</Notice>}

      <Card
        title="Windows"
        extra={can('TECH_ADMIN') ? (
          <button type="button" className="secondary small" disabled={busy} onClick={purgeNow}>
            Run the purge now
          </button>
        ) : undefined}
      >
        {policies.loading && !policies.data && <Loading />}
        {policies.error && <Notice kind="error">{policies.error}</Notice>}
        {policies.data?.length === 0 && <Empty>No retention policies are configured.</Empty>}

        {policies.data && policies.data.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Class</th><th>What it holds</th><th className="num">Days</th>
                  <th className="num">Stored</th><th>Updated</th>
                  {can('TECH_ADMIN') && <th>Change</th>}
                </tr>
              </thead>
              <tbody>
                {policies.data.map((policy) => {
                  const edit = edits[policy.eventClass];
                  return (
                    <tr key={policy.eventClass}>
                      <td><strong>{policy.eventClass.toLowerCase()}</strong></td>
                      <td style={{ fontSize: 12.5, color: 'var(--muted)', maxWidth: '44ch' }}>
                        {CLASS_DESCRIPTIONS[policy.eventClass]}
                      </td>
                      <td className="num">{policy.retentionDays}</td>
                      <td className="num">{policy.storedEvents.toLocaleString()}</td>
                      <td style={{ fontSize: 12.5 }}>{exactly(policy.updatedAt)}</td>
                      {can('TECH_ADMIN') && (
                        <td style={{ minWidth: 280 }}>
                          {!edit ? (
                            <button
                              type="button"
                              className="secondary small"
                              onClick={() =>
                                setEdits((current) => ({
                                  ...current,
                                  [policy.eventClass]: { days: String(policy.retentionDays), justification: '' },
                                }))
                              }
                            >
                              Change
                            </button>
                          ) : (
                            <div style={{ display: 'grid', gap: 6 }}>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={edit.days}
                                onChange={(event) =>
                                  setEdits((c) => ({ ...c, [policy.eventClass]: { ...edit, days: event.target.value } }))
                                }
                              />
                              <input
                                type="text"
                                placeholder="Why this window is changing (required)"
                                value={edit.justification}
                                onChange={(event) =>
                                  setEdits((c) => ({
                                    ...c,
                                    [policy.eventClass]: { ...edit, justification: event.target.value },
                                  }))
                                }
                              />
                              {Number(edit.days) < policy.retentionDays && (
                                <span style={{ color: 'var(--stop)', fontSize: 12 }}>
                                  Shortening this window deletes data at the next purge.
                                </span>
                              )}
                              <div className="actions">
                                <button
                                  type="button"
                                  className="small"
                                  disabled={busy || edit.justification.trim().length < 10 || !Number(edit.days)}
                                  onClick={() => save(policy.eventClass)}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="secondary small"
                                  onClick={() =>
                                    setEdits((current) => {
                                      const next = { ...current };
                                      delete next[policy.eventClass];
                                      return next;
                                    })
                                  }
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
